/**
 * useDashboardRings Hook
 *
 * Fetches all data needed for the three performance rings and calculates scores.
 * Rings: Doorstroom (Velocity), Vandaag (Flow), Liquiditeit (Liquidity)
 *
 * SCOPE: Sales-only dealerships. Garage metrics are OUT OF SCOPE for v1.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CACHE_24_HOURS } from '@/lib/constants/cache';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useActiveTeamId } from '@/contexts/TeamContext';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardRingSettings } from './useDashboardRingSettings';
import { useDashboardRingStateLive } from './useDashboardRingStateLive';
import {
  calculateAllRings,
  type VehicleForRing,
  type SalesInvoiceForRing,
  type ManualTaskForRing,
  type RingCalculationResult,
} from '@/lib/dashboard/ringCalculations';
import { DEFAULT_DASHBOARD_RING_SETTINGS } from '@/features/settings/settings.schema';

// DEV-only logger to satisfy eslint no-console without changing logic
const devLog = (...args: unknown[]) => {
  if (!import.meta.env.DEV) return;
  (globalThis as Record<string, unknown>)?.console?.log?.(...args);
};
const devWarn = (...args: unknown[]) => {
  if (!import.meta.env.DEV) return;
  (globalThis as Record<string, unknown>)?.console?.warn?.(...args);
};

// ============================================================================
// Query Keys
// ============================================================================

export const dashboardRingsKeys = {
  all: ['dashboardRings'] as const,
  byTeam: (teamId: string) => [...dashboardRingsKeys.all, teamId] as const,
};

// ============================================================================
// Safe Date Conversion Helper
// ============================================================================

/**
 * Safely convert Firestore Timestamp or date-like value to Date
 * Never throws - returns undefined if conversion fails
 */
function safeToDate(value: unknown): Date | undefined {
  if (!value) return undefined;

  try {
    // Firestore Timestamp with toDate method
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
      const result = (value as { toDate: () => Date }).toDate();
      if (result instanceof Date && !isNaN(result.getTime())) {
        return result;
      }
    }

    // Already a Date
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }

    // String or number - try to parse
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  } catch {
    // Conversion failed - return undefined
  }

  return undefined;
}

// ============================================================================
// Data Fetching
// ============================================================================

/**
 * Fetch active vehicles for Doorstroom calculation
 */
async function fetchVehiclesForRings(teamId: string): Promise<VehicleForRing[]> {
  const vehiclesRef = collection(db, 'teams', teamId, 'vehicles');

  // Fetch vehicles that are not sold and not deleted
  const q = query(
    vehiclesRef,
    where('status', 'in', ['available', 'reserved', 'purchased', 'maintenance'])
  );

  const snapshot = await getDocs(q);

  return snapshot.docs
    .filter((doc) => doc.data().deletedAt == null)
    .map((doc) => {
      const data = doc.data();

      // Determine "in stock since" date using safe conversion
      // Priority: financialSummary.purchasedAt (from invoice) > purchaseDate (manual) > createdAt > now
      // financialSummary.purchasedAt is the canonical source (computed from purchase invoices)
      const inStockSince =
        safeToDate(data.financialSummary?.purchasedAt) ||
        safeToDate(data.purchaseDate) ||
        safeToDate(data.createdAt) ||
        new Date(); // Fallback to now if all missing

      // Get capital locked value for value-weighted scoring
      // Priority: financialSummary.capitalLockedCents > purchasePrice (converted to cents)
      const capitalLockedCents = data.financialSummary?.capitalLockedCents;

      // Handle purchasePrice - stored in cents in the database
      // The financialSummary.purchasePriceCents is the canonical source
      let purchasePriceCents: number | undefined;
      if (data.financialSummary?.purchasePriceCents) {
        purchasePriceCents = data.financialSummary.purchasePriceCents as number;
      } else if (data.purchasePrice) {
        // Legacy fallback: purchasePrice field (assume cents if > 100k, else euros)
        const rawPrice = data.purchasePrice as number;
        purchasePriceCents = rawPrice > 100_000 ? Math.round(rawPrice) : Math.round(rawPrice * 100);
      }

      return {
        id: doc.id,
        licensePlate: data.licensePlate || '',
        brand: data.brand || '',
        model: data.model || '',
        status: data.status || 'available',
        inStockSince,
        capitalLockedCents,
        purchasePriceCents,
      };
    });
}

/**
 * Fetch sales invoices for Vandaag and Liquiditeit calculations
 */
async function fetchSalesInvoicesForRings(teamId: string): Promise<SalesInvoiceForRing[]> {
  const invoicesRef = collection(db, 'teams', teamId, 'salesInvoices');

  // Fetch non-cancelled, non-deleted invoices
  const q = query(invoicesRef, where('status', 'in', ['draft', 'sent', 'paid', 'overdue']));

  const snapshot = await getDocs(q);

  return snapshot.docs
    .filter((doc) => doc.data().deletedAt == null)
    .map((doc) => {
      const data = doc.data();

      // Use safe date conversion - invoiceDate falls back to now if missing
      const invoiceDate = safeToDate(data.invoiceDate) || new Date();

      return {
        id: doc.id,
        invoiceNo: data.invoiceNo || `#${doc.id.slice(0, 6)}`,
        status: data.status as SalesInvoiceForRing['status'],
        partyName: data.partyName || data.customerName,
        invoiceDate,
        dueDate: safeToDate(data.dueDate),
        sentAt: safeToDate(data.sentAt),
        totalCents: data.total || data.totalCents || 0,
        isQuote: false,
        followUpDismissed: data.followUpDismissed,
        // Vandaag v2 fields
        readyToSend: data.readyToSend === true,
        snoozedUntil: data.snoozedUntil,
        isDismissed: data.isDismissed === true,
        // Liquiditeit reminder snooze fields
        reminderSnoozedUntil: safeToDate(data.reminderSnoozedUntil),
        partyEmail: data.partyEmail as string | undefined,
        partyPhone: data.partyPhone as string | undefined,
        partyId: data.partyId as string | undefined,
      };
    });
}

/**
 * Convert a task document to ManualTaskForRing
 */
function docToManualTaskForRing(
  docId: string,
  data: Record<string, unknown>,
  source: 'team' | 'personal'
): ManualTaskForRing {
  // Handle both dueAt (old) and dueDate (new schema) field names
  // Use safe date conversion
  const dueAt = safeToDate(data.dueDate) || safeToDate(data.dueAt);
  const completedAt = safeToDate(data.completedAt);

  return {
    id: docId, // Raw ID - scope is separate
    title: (data.title as string) || 'Taak',
    status: data.status as 'open' | 'done',
    dueAt,
    completedAt,
    // Vandaag v2 fields
    snoozedUntil: data.snoozedUntil as string | undefined,
    isDismissed: data.isDismissed === true,
    // Scope for ring state key generation
    scope: source === 'team' ? 'team' : 'user',
  };
}

/**
 * Fetch team tasks for Vandaag calculation
 * Reads from /teams/{teamId}/tasks (team-visible)
 */
async function fetchTeamTasksForRings(teamId: string): Promise<ManualTaskForRing[]> {
  try {
    const tasksRef = collection(db, 'teams', teamId, 'tasks');

    // Fetch open and done tasks (dismissed tasks are excluded from calculation)
    const q = query(tasksRef, where('status', 'in', ['open', 'done']));

    const snapshot = await getDocs(q);

    return snapshot.docs
      .filter((doc) => doc.data().deletedAt == null)
      .map((doc) => docToManualTaskForRing(doc.id, doc.data(), 'team'));
  } catch (error) {
    // If team tasks read fails (permissions), return empty array
    // This allows Doorstroom/Liquiditeit to still work
    devWarn('[DashboardRings] Failed to fetch team tasks, using empty array:', error);
    return [];
  }
}

/**
 * Fetch personal tasks for Vandaag calculation
 * Reads from /users/{uid}/tasks (personal tasks)
 */
async function fetchPersonalTasksForRings(userId: string): Promise<ManualTaskForRing[]> {
  try {
    const tasksRef = collection(db, 'users', userId, 'tasks');

    // Fetch open and done tasks
    const q = query(tasksRef, where('status', 'in', ['open', 'done']));

    const snapshot = await getDocs(q);

    return snapshot.docs
      .filter((doc) => doc.data().deletedAt == null)
      .map((doc) => docToManualTaskForRing(doc.id, doc.data(), 'personal'));
  } catch (error) {
    devWarn('[DashboardRings] Failed to fetch personal tasks, using empty array:', error);
    return [];
  }
}

/**
 * Fetch ALL tasks (team + personal) for Vandaag calculation
 */
async function fetchAllTasksForRings(teamId: string, userId: string): Promise<ManualTaskForRing[]> {
  const [teamTasks, personalTasks] = await Promise.all([
    fetchTeamTasksForRings(teamId),
    fetchPersonalTasksForRings(userId),
  ]);

  const mergedTasks = [...teamTasks, ...personalTasks];

  // DEV logging
  if (import.meta.env.DEV) {
    const tasksWithDueDate = mergedTasks.filter((t) => t.dueAt);
    const tasksWithoutDueDate = mergedTasks.filter((t) => !t.dueAt);

    devLog('[DashboardRings] Task fetch summary:', {
      teamTasksCount: teamTasks.length,
      personalTasksCount: personalTasks.length,
      mergedTasksCount: mergedTasks.length,
      tasksWithDueDate: tasksWithDueDate.length,
      tasksWithoutDueDate: tasksWithoutDueDate.length,
    });

    if (tasksWithoutDueDate.length > 0) {
      devWarn(
        '[DashboardRings] Tasks without due date (will be excluded from Vandaag):',
        tasksWithoutDueDate.map((t) => ({ id: t.id, title: t.title }))
      );
    }
  }

  return mergedTasks;
}

// ============================================================================
// Main Hook
// ============================================================================

export interface DashboardRingsResult extends RingCalculationResult {
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Default ring result for loading/error states */
const DEFAULT_RINGS_RESULT: Omit<DashboardRingsResult, 'isLoading' | 'error' | 'refetch'> = {
  rings: {
    doorstroom: { id: 'doorstroom', label: 'Doorstroom', score: 0, color: 'green' },
    vandaag: { id: 'vandaag', label: 'Vandaag', score: 0, color: 'green' },
    liquiditeit: { id: 'liquiditeit', label: 'Liquiditeit', score: 0, color: 'green' },
  },
  explanations: [],
  nudges: [],
};

/**
 * Hook to fetch and calculate dashboard ring scores
 * Non-throwing: returns safe defaults if team/user context not ready
 */
export function useDashboardRings(): DashboardRingsResult {
  const teamId = useActiveTeamId(); // Non-throwing
  const { user } = useAuth();
  const userId = user?.uid;

  const { data: settings, isLoading: settingsLoading } = useDashboardRingSettings();

  // Live ring state from Firestore onSnapshot (team + user)
  const { combinedStateMap: ringStateMap, isLoading: ringStateLoading } =
    useDashboardRingStateLive();

  // Fetch raw data (without ring state filtering - that happens reactively)
  const {
    data: rawData,
    isLoading: dataLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: dashboardRingsKeys.byTeam(teamId || 'none'),
    queryFn: async () => {
      // Fetch all data in parallel
      // Note: manualTasks now includes BOTH team tasks AND personal tasks
      const [vehicles, salesInvoices, manualTasks] = await Promise.all([
        fetchVehiclesForRings(teamId!),
        fetchSalesInvoicesForRings(teamId!),
        fetchAllTasksForRings(teamId!, userId!), // Team + Personal tasks
      ]);

      // Calculate total inventory value for Liquiditeit exposure calculation
      const inventoryValueCents = vehicles.reduce((sum, v) => {
        const value = v.capitalLockedCents || v.purchasePriceCents || 0;
        return sum + value;
      }, 0);

      return { vehicles, salesInvoices, manualTasks, inventoryValueCents };
    },
    enabled: !!teamId && !!userId && !settingsLoading,
    staleTime: CACHE_24_HOURS,
    gcTime: CACHE_24_HOURS,
  });

  // REACTIVE: Calculate rings with live ringStateMap filtering
  // This useMemo re-runs whenever ringStateMap changes (from onSnapshot)
  // This is the key fix for live Vandaag % updates
  const ringsData = useMemo(() => {
    if (!rawData) return null;

    const { vehicles, salesInvoices, manualTasks, inventoryValueCents } = rawData;
    const ringSettings = settings || DEFAULT_DASHBOARD_RING_SETTINGS;

    // DEV logging for data summary
    if (import.meta.env.DEV) {
      devLog('[DashboardRings] Calculating with live ringStateMap:', {
        vehicles: vehicles.length,
        invoices: salesInvoices.length,
        tasks: manualTasks.length,
        ringStateItems: ringStateMap?.size || 0,
        inventoryValueEur: (inventoryValueCents / 100).toFixed(2),
      });
    }

    // Calculate all ring scores with ring state filtering
    const result = calculateAllRings({
      vehicles,
      salesInvoices,
      manualTasks,
      settings: ringSettings,
      inventoryValueCents,
      ringStateMap,
    });

    // DEV logging for ring scores
    if (import.meta.env.DEV) {
      devLog('[DashboardRings] CALCULATED SCORES:', {
        doorstroom: result.rings.doorstroom.score,
        vandaag: result.rings.vandaag.score,
        liquiditeit: result.rings.liquiditeit.score,
      });
    }

    return result;
  }, [rawData, settings, ringStateMap]);

  // Return safe defaults while loading or if context not ready
  // CRITICAL: isLoading must be true whenever we return DEFAULT_RINGS_RESULT
  // to prevent 0-score flash during startup
  if (!teamId || !userId || !ringsData) {
    return {
      ...DEFAULT_RINGS_RESULT,
      // Always show loading when returning defaults - prevents 0-score flash
      isLoading: true,
      error: error,
      refetch,
    };
  }

  return {
    isLoading: false,
    error: error,
    refetch,
    ...ringsData,
  };
}
